/**
 * usePanZoom — the ONE viewport for every spatial surface.
 *
 * Harvested from OrgChart (the best of the ~10 forked pan/zoom
 * implementations in this repo, per CANVAS-AUTHORING-ADVERSARIAL-REVIEW-2026-09
 * §H) so the others can converge on it instead of each reinventing a worse
 * copy. What OrgChart had and GoalCanvas did not:
 *
 *   - two-pointer PINCH + two-finger pan (GoalCanvas advertised pinch in its
 *     header comment and did not implement it — dead on every touch device,
 *     because `touch-action: none` also suppresses the native gesture)
 *   - `deltaMode` normalisation (a Firefox/line-mode wheel scrolled ~16x short)
 *   - a clamped per-event wheel delta (one mouse notch was a huge jump)
 *   - eased, interruptible transitions for PROGRAMMATIC moves, with a
 *     convergence guarantee if rAF is throttled, honouring reduced-motion
 *
 * Direct manipulation (drag, wheel, pinch) is always instant — you never
 * animate a gesture. Only buttons/fit/focus animate.
 *
 * The consumer owns what counts as a pan: `shouldPan` receives the pointerdown
 * and decides. That is the seam marquee-select needs — a board can hand pan to
 * space/middle-drag and keep plain background drag for selection.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
const DEFAULT_VIEW = { x: 0, y: 0, z: 1 };
const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
const mid = (a, b) => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });
export function usePanZoom(options = {}) {
    const { initial = DEFAULT_VIEW, minZoom = 0.25, maxZoom = 2.5, storageKey, shouldPan, ignoreWheelWithin, } = options;
    const clampZoom = useCallback((z) => Math.min(maxZoom, Math.max(minZoom, z)), [minZoom, maxZoom]);
    const load = () => {
        if (!storageKey)
            return initial;
        try {
            const raw = typeof window !== 'undefined' ? window.localStorage.getItem(storageKey) : null;
            if (raw) {
                const v = JSON.parse(raw);
                if (Number.isFinite(v?.x) && Number.isFinite(v?.y) && Number.isFinite(v?.z)) {
                    return { x: v.x, y: v.y, z: Math.min(maxZoom, Math.max(minZoom, v.z)) };
                }
            }
        }
        catch { /* fall through to the initial view */ }
        return initial;
    };
    const [view, setView] = useState(load);
    const viewRef = useRef(view);
    viewRef.current = view;
    const containerRef = useRef(null);
    const [panning, setPanning] = useState(false);
    const [pinching, setPinching] = useState(false);
    const panRef = useRef(null);
    const pointersRef = useRef(new Map());
    const pinchRef = useRef(null);
    useEffect(() => {
        if (!storageKey)
            return;
        try {
            window.localStorage.setItem(storageKey, JSON.stringify(view));
        }
        catch { /* best effort */ }
    }, [view, storageKey]);
    // ── programmatic, eased moves ──────────────────────────────────────────
    const animRef = useRef(0);
    const animTimerRef = useRef(undefined);
    const reduceMotion = () => typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const animateViewTo = useCallback((target, ms = 380) => {
        cancelAnimationFrame(animRef.current);
        if (animTimerRef.current)
            clearTimeout(animTimerRef.current);
        if (ms <= 0 || reduceMotion()) {
            setView(target);
            return;
        }
        const start = { ...viewRef.current };
        const t0 = performance.now();
        const ease = (t) => 1 - Math.pow(1 - t, 3); // easeOutCubic
        const step = (now) => {
            const p = Math.min(1, (now - t0) / ms);
            const e = ease(p);
            setView({
                x: start.x + (target.x - start.x) * e,
                y: start.y + (target.y - start.y) * e,
                z: start.z + (target.z - start.z) * e,
            });
            if (p < 1)
                animRef.current = requestAnimationFrame(step);
        };
        animRef.current = requestAnimationFrame(step);
        // If rAF is throttled (background tab), still land exactly on target so the
        // control never looks dead. Harmless no-op on a normal completion.
        animTimerRef.current = setTimeout(() => setView(target), ms + 80);
    }, []);
    useEffect(() => () => {
        cancelAnimationFrame(animRef.current);
        if (animTimerRef.current)
            clearTimeout(animTimerRef.current);
    }, []);
    // ── zoom ───────────────────────────────────────────────────────────────
    /** Zoom keeping the WORLD point under (clientX, clientY) fixed. */
    const zoomAt = useCallback((clientX, clientY, factor) => {
        setView((v) => {
            const vp = containerRef.current;
            const nz = clampZoom(v.z * factor);
            if (!vp || nz === v.z)
                return nz === v.z ? v : { ...v, z: nz };
            const rect = vp.getBoundingClientRect();
            const px = clientX - rect.left;
            const py = clientY - rect.top;
            const k = nz / v.z;
            return { z: nz, x: px - (px - v.x) * k, y: py - (py - v.y) * k };
        });
    }, [clampZoom]);
    const zoomBy = useCallback((factor) => {
        const vp = containerRef.current;
        const v = viewRef.current;
        const nz = clampZoom(v.z * factor);
        if (nz === v.z)
            return;
        const cxp = vp ? vp.clientWidth / 2 : 0;
        const cyp = vp ? vp.clientHeight / 2 : 0;
        const k = nz / v.z;
        animateViewTo({ z: nz, x: cxp - (cxp - v.x) * k, y: cyp - (cyp - v.y) * k }, 200);
    }, [animateViewTo, clampZoom]);
    const reset = useCallback(() => animateViewTo(initial, 300), [animateViewTo, initial]);
    const fitTo = useCallback((b, opts = {}) => {
        const vp = containerRef.current;
        if (!vp || vp.clientWidth < 40)
            return;
        const pad = opts.pad ?? 40;
        const w = Math.max(1, (b.maxX - b.minX) + pad * 2);
        const h = Math.max(1, (b.maxY - b.minY) + pad * 2);
        const z = clampZoom(Math.min(opts.maxZoom ?? 1, Math.min(vp.clientWidth / w, vp.clientHeight / h)));
        const target = {
            z,
            x: (vp.clientWidth - w * z) / 2 - (b.minX - pad) * z,
            y: (vp.clientHeight - h * z) / 2 - (b.minY - pad) * z,
        };
        if (opts.animate === false)
            setView(target);
        else
            animateViewTo(target, 320);
    }, [animateViewTo, clampZoom]);
    // ── coordinate transforms ──────────────────────────────────────────────
    const screenToWorld = useCallback((clientX, clientY) => {
        const el = containerRef.current;
        const v = viewRef.current;
        if (!el)
            return { x: 0, y: 0 };
        const r = el.getBoundingClientRect();
        return { x: (clientX - r.left - v.x) / v.z, y: (clientY - r.top - v.y) / v.z };
    }, []);
    const worldToScreen = useCallback((x, y) => {
        const v = viewRef.current;
        return { x: x * v.z + v.x, y: y * v.z + v.y };
    }, []);
    // ── wheel: native + non-passive so page scroll can be prevented ────────
    useEffect(() => {
        const vp = containerRef.current;
        if (!vp)
            return undefined;
        const onWheel = (e) => {
            if (ignoreWheelWithin && e.target?.closest?.(ignoreWheelWithin))
                return;
            e.preventDefault();
            let dy = e.deltaY;
            let dx = e.deltaX;
            if (e.deltaMode === 1) {
                dy *= 16;
                dx *= 16;
            } // lines → px
            if (e.ctrlKey || e.metaKey) {
                // Browsers report a trackpad pinch as ctrl+wheel. Clamp the per-event
                // delta so one mouse notch is a step, not a leap.
                zoomAt(e.clientX, e.clientY, Math.exp(-Math.max(-60, Math.min(60, dy)) * 0.0016));
            }
            else {
                setView((v) => ({ ...v, x: v.x - dx, y: v.y - dy }));
            }
        };
        vp.addEventListener('wheel', onWheel, { passive: false });
        return () => vp.removeEventListener('wheel', onWheel);
    }, [zoomAt, ignoreWheelWithin]);
    // ── pointer: one finger pans, two pinch ────────────────────────────────
    const onPointerDown = (e) => {
        const second = pointersRef.current.size === 1;
        // A second finger always becomes a pinch, even if the first gesture was
        // declined for panning (e.g. it started on a card).
        if (!second && shouldPan && !shouldPan(e))
            return;
        pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
        if (pointersRef.current.size === 1) {
            panRef.current = { startX: e.clientX, startY: e.clientY, ox: viewRef.current.x, oy: viewRef.current.y, pointerId: e.pointerId };
            setPanning(true);
        }
        else if (pointersRef.current.size === 2) {
            panRef.current = null;
            const [p1, p2] = [...pointersRef.current.values()];
            pinchRef.current = { startDist: dist(p1, p2) || 1, startMid: mid(p1, p2), startView: viewRef.current };
            setPanning(false);
            setPinching(true);
        }
        try {
            e.currentTarget.setPointerCapture?.(e.pointerId);
        }
        catch { /* non-active pointer */ }
    };
    const onPointerMove = (e) => {
        if (!pointersRef.current.has(e.pointerId))
            return;
        pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
        const anchor = pinchRef.current;
        if (pointersRef.current.size >= 2 && anchor) {
            // Recompute from the FROZEN start view each move so the gesture cannot
            // drift; translate by midpoint travel so two fingers also pan.
            const [p1, p2] = [...pointersRef.current.values()];
            const d = dist(p1, p2);
            const m = mid(p1, p2);
            const rect = containerRef.current?.getBoundingClientRect();
            const s = anchor;
            const nz = clampZoom(s.startView.z * (d / s.startDist));
            const k = nz / s.startView.z;
            const smx = s.startMid.x - (rect?.left || 0);
            const smy = s.startMid.y - (rect?.top || 0);
            setView({
                z: nz,
                x: smx - (smx - s.startView.x) * k + (m.x - s.startMid.x),
                y: smy - (smy - s.startView.y) * k + (m.y - s.startMid.y),
            });
            return;
        }
        const p = panRef.current;
        if (!p)
            return;
        setView((v) => ({ ...v, x: p.ox + (e.clientX - p.startX), y: p.oy + (e.clientY - p.startY) }));
    };
    const endPointer = (e) => {
        pointersRef.current.delete(e.pointerId);
        if (pointersRef.current.size < 2) {
            pinchRef.current = null;
            setPinching(false);
        }
        if (pointersRef.current.size === 0) {
            panRef.current = null;
            setPanning(false);
        }
        else if (pointersRef.current.size === 1) {
            // Dropped from pinch back to one finger — resume panning from here.
            const [p] = [...pointersRef.current.entries()];
            panRef.current = { startX: p[1].x, startY: p[1].y, ox: viewRef.current.x, oy: viewRef.current.y, pointerId: p[0] };
            setPanning(true);
        }
    };
    return {
        view,
        viewRef,
        setView,
        containerRef,
        panning,
        pinching,
        zoomAt,
        zoomBy,
        animateViewTo,
        reset,
        fitTo,
        screenToWorld,
        worldToScreen,
        handlers: {
            onPointerDown,
            onPointerMove,
            onPointerUp: endPointer,
            onPointerCancel: endPointer,
        },
    };
}
export default usePanZoom;
